import React from 'react';
import BaseCard from '../../../../Dashboard/components/BaseCard';
import { ReactComponent as CapsuleIcon } from '../../../../../images/icons/capsule.svg';
import { ReactComponent as CapsuleFillIcon } from '../../../../../images/icons/capsule_fill.svg';
import { Box } from '@mui/material';
import { CustomLegend } from '../../../../Dashboard/components/CustomLegend';
import { Cell, PieChart, Pie } from 'recharts';
import {
  black,
  customError,
  lightBackground1,
  lightGrey,
  warningYellow,
} from '../../../../../mainTheme';

interface AdherenceCardProps {
  data: {
    adherent: number;
    nonAdherent: number;
    unknown: number;
  };
  isSelected?: boolean;
  onClick?: () => void;
}

// Config for legend and chart
const ADHERENCE_CONFIG = [
  { name: 'non-adherent', key: 'nonAdherent', color: customError },
  { name: 'unknown', key: 'unknown', color: warningYellow },
  { name: 'adherent', key: 'adherent', color: '#41AF60' },
];

const NO_DATA_COLOR = lightGrey;

const AdherenceCard: React.FC<AdherenceCardProps> = ({
  data,
  isSelected,
  onClick,
}) => {
  const total = data.adherent + data.nonAdherent + data.unknown;
  const isNoData = total === 0;

  // Rounding logic
  let percentages = {
    adherent: total ? (data.adherent / total) * 100 : 0,
    nonAdherent: total ? (data.nonAdherent / total) * 100 : 0,
    unknown: total ? (data.unknown / total) * 100 : 0,
  };

  let roundedPercentages = {
    adherent: Math.round(percentages.adherent),
    nonAdherent: Math.round(percentages.nonAdherent),
    unknown: Math.round(percentages.unknown),
  };

  let sum =
    roundedPercentages.adherent +
    roundedPercentages.nonAdherent +
    roundedPercentages.unknown;
  let diff = 100 - sum;

  if (diff !== 0) {
    let maxKey = Object.keys(roundedPercentages).reduce((a, b) =>
      roundedPercentages[a as keyof typeof roundedPercentages] >
      roundedPercentages[b as keyof typeof roundedPercentages]
        ? a
        : b,
    ) as keyof typeof roundedPercentages;
    roundedPercentages[maxKey] += diff;
  }

  const chartData = ADHERENCE_CONFIG.map((category) => ({
    name: category.name,
    value: isNoData
      ? category.key === 'adherent'
        ? 1
        : 0
      : roundedPercentages[category.key as keyof typeof roundedPercentages],
    color: category.color,
  }));

  return (
    <BaseCard
      title="Adherence"
      isSelected={isSelected}
      onClick={onClick}
      icon={isSelected ? <CapsuleFillIcon /> : <CapsuleIcon />}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <CustomLegend
          payload={chartData.map((entry) => ({
            name: entry.name,
            color: entry.color,
            value: isNoData || entry.value === 0 ? 'N/A' : `${entry.value}%`,
          }))}
        />
        <PieChart width={80} height={80} style={{ flexShrink: 0 }}>
          <circle
            cx="40"
            cy="40"
            r="18"
            fill={isNoData ? NO_DATA_COLOR : '#B5E3C2'}
          />
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={22}
            outerRadius={40}
            dataKey="value"
            startAngle={15}
            endAngle={450}
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={isNoData ? NO_DATA_COLOR : entry.color}
              />
            ))}
          </Pie>
          <text
            x="50%"
            y="50%"
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={12}
            fill={isNoData ? lightBackground1 : black}
          >
            {isNoData ? 'N/A' : `${roundedPercentages.adherent}%`}
          </text>
        </PieChart>
      </Box>
    </BaseCard>
  );
};

export default AdherenceCard;
